import { useCallback, useState } from 'react';
import { useStore, useToast } from 'hooks';
import { useEffect } from 'react';

// A same-tap adjust or leveling refresh bumps tapHistoryRequest after history resolves.
// The line and item ids stay put, so this load has to finish on its own token.
let pageLoadRequest = 0;

const useTapDetailsData = () => {
  const { linesStore, itemsStore, itemLinesStore, itemEventsStore } = useStore();
  const { errorToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadItemData = useCallback(async () => {
    try {
      await Promise.all([
        itemsStore.fetchItem(linesStore.currentLine.item.id),
        itemLinesStore.fetchItemLine(linesStore.currentLine.id),
      ]);
    } catch (err) {
      errorToast(err);
    }
  }, [
    errorToast,
    itemLinesStore,
    itemsStore,
    linesStore.currentLine.id,
    linesStore.currentLine?.item?.id,
  ]);

  const loadData = useCallback(async () => {
    const lineId = linesStore.currentLine?.id;
    const request = ++pageLoadRequest;
    const current = () => request === pageLoadRequest;

    try {
      setIsLoading(true);
      itemEventsStore.clearTapHistory();
      await linesStore.fetchLine(lineId);
      if (!current()) return;

      const line = linesStore.getLineById(lineId);
      const tapId = line?.lineTap?.tap_id;
      // Not awaited. A rejected audit stays in the History panel and must not blank the dashboards.
      if (Boolean(tapId)) {
        itemEventsStore.fetchTapHistory(tapId).catch(err => console.error(err));
      }

      if (!line?.item?.id) {
        setIsLoaded(true);
        setIsLoading(false);
        return;
      }

      const itemRequest = loadItemData();
      if (line._item_lines_latest_item_id === line.item.id) {
        setIsLoading(false);
        await itemRequest;
      } else {
        await itemRequest;
        if (!current()) return;
        setIsLoading(false);
      }
      if (!current()) return;
      setIsLoaded(true);
    } catch (err) {
      if (!current()) return;
      setIsLoading(false);
      errorToast(err);
    }
  }, [linesStore, loadItemData, errorToast, itemEventsStore]);

  useEffect(() => {
    (async () => {
      await loadData();
    })();
  }, [linesStore.currentLine?.id, linesStore.currentLine?.item?.id, loadData]);

  return { isLoading, isLoaded, loadData };
};

export default useTapDetailsData;
